/**
 * Runtime brand theme (DESIGN.md §3.2, §16).
 *
 * The platform API generates the brand tokens (contrast-checked for both themes, see
 * @platform/branding) and the apps apply them here as CSS variables: --brand, --brand-foreground,
 * --accent and the --brand-50 … --brand-950 scale. Everything that uses the brand (primary, ring,
 * sidebar-primary) follows automatically. Status colours stay fixed.
 *
 * The last branding is cached in localStorage so the next page load paints with it before the
 * API answers (no flash of the default theme).
 */
import type { BrandColors, BrandTheme, PublicBranding } from '@platform/branding';

const STYLE_ID = 'platform-brand-theme';
const CACHE_KEY = 'platform.branding';

/** Only `#rrggbb`, so a theme value can never inject other CSS. */
const SAFE_COLOR = /^#[0-9a-f]{6}$/i;

function vars(colors: BrandColors, scale?: BrandTheme['scale']): string {
  const values = [colors.brand, colors.brandForeground, colors.accent, ...Object.values(scale ?? {})];
  const bad = values.find((value) => !SAFE_COLOR.test(value));
  if (bad !== undefined) throw new Error(`Invalid brand color: ${bad}`);
  const steps = Object.entries(scale ?? {})
    .map(([step, value]) => `--brand-${step}:${value};`)
    .join('');
  return `--brand:${colors.brand};--brand-foreground:${colors.brandForeground};--accent:${colors.accent};${steps}`;
}

/**
 * CSS for a brand theme. `scope` limits it to a subtree (live preview), e.g.
 * `[data-brand-preview="x"]`; without it the whole document is themed.
 */
export function brandThemeCss(theme: BrandTheme, scope?: string): string {
  const light = scope ? `${scope}.light,${scope} .light` : ':root,.light';
  const dark = scope ? `${scope}.dark,${scope} .dark` : '.dark';
  return `${light}{${vars(theme.light, theme.scale)}}${dark}{${vars(theme.dark)}}`;
}

/** Applies (or with `null` removes) the brand theme for the whole document. */
export function applyBrandTheme(theme: BrandTheme | null, doc: Document = document): void {
  const css = theme ? brandThemeCss(theme) : null;
  doc.getElementById(STYLE_ID)?.remove();
  if (css === null) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = css;
  doc.head.appendChild(style);
}

/** Last branding seen (for the first paint of the next page load). */
export function readCachedBranding(): PublicBranding | null {
  try {
    const raw = globalThis.localStorage?.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as PublicBranding) : null;
  } catch {
    return null;
  }
}

export function cacheBranding(branding: PublicBranding): void {
  try {
    globalThis.localStorage?.setItem(CACHE_KEY, JSON.stringify(branding));
  } catch {
    // Not cached; the next load paints the default theme until the API answers.
  }
}

/** Sets the browser tab icon (`null` restores the app default). */
export function applyFavicon(href: string | null, doc: Document = document): void {
  let link = doc.querySelector<HTMLLinkElement>('link[rel="icon"][data-platform]');
  if (!href) {
    link?.remove();
    return;
  }
  if (!link) {
    link = doc.createElement('link');
    link.rel = 'icon';
    link.dataset.platform = 'branding';
    doc.head.appendChild(link);
  }
  link.href = href;
}
